import { useEffect, useMemo, useRef, useState } from "react";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Form from "@ui-kit/form/Form/Form.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useTranslation } from "@shared/utils/internationalization";
import { common } from "@shared/constants";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import useDebounce from "@shared/hooks/useDebounce";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import AssignedSeatCartItem from "../AssignedSeatCartItem/AssignedSeatCartItem.web";
import { LocationCartItemProps } from "./LocationCartItem.props";

const LocationCartItem = ({
  permission,
  item,
  isDisabled,
}: LocationCartItemProps) => {
  const { t } = useTranslation("seating");
  const { isBasketUpdating } = usePublicBasket();
  const { actions, seatingServiceState, getInfoRequiredLocationQtyInCart } =
    useSeatingState();

  const itemId: number = item.type ?? item.membership_level;

  const [locationQty, setLocationQty] = useState(
    permission.location?.count ?? 0
  );

  const allowRequest = useRef(false);
  const [debouncedQuantity] = useDebounce(locationQty, 500);

  /**
   * For item groups that ARE NOT split,
   * Update the quantity every time the debounced quantity changes.
   * But only when the quantity is allowed to be updated (ex: the incrementor value has been changed)
   */
  useEffect(() => {
    if (allowRequest.current) {
      // use .then() and .catch() syntax
      // otherwise the error isn't caught properly
      actions
        .initModifyLocationPermissions(permission, item, debouncedQuantity)
        .catch((error) => {
          recordAppError(error);
        })
        .finally(() => {
          allowRequest.current = false;
        });
    }
  }, [debouncedQuantity, item, permission, actions]);

  /**
   * For item groups that ARE split,
   * The final item group quantity after removal will be:
   * The total item quantity in the cart decremented by 1
   */
  const decrementedItemGroupQty = useMemo(() => {
    // default final quantity is 0
    let finalQuantity = 0;
    const itemId = item.type ?? item.membership_level;

    // info_required items are split in the cart
    // to remove one, we need to reduce the total quantity by - 1
    if (item.info_required && itemId) {
      finalQuantity = getInfoRequiredLocationQtyInCart(
        itemId,
        permission.id,
        1
      );
      // reduce quantity by 1
      finalQuantity -= 1;
    }

    return finalQuantity;
  }, [
    getInfoRequiredLocationQtyInCart,
    item.type,
    item.membership_level,
    item.info_required,
    permission.id,
  ]);

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <AssignedSeatCartItem
        location_identifier={permission.location?.identifier}
        segment_identifier={permission.location?.segment_identifier}
        quantity={permission.location?.count}
      />
      {!item.info_required && (
        <Form.Incrementer
          data-testid="location-cart-item-incrementor"
          incrementerVariant="input"
          value={locationQty}
          min={
            seatingServiceState?.itemTypeMap[itemId]?.minimum_purchase_limit ??
            common.DEFAULT_MIN_TICKET_PURCHASE_LIMIT
          }
          max={
            seatingServiceState?.itemTypeMap[itemId]?.purchase_limit ??
            common.DEFAULT_MAX_TICKET_PURCHASE_LIMIT
          }
          isDisabled={isDisabled}
          isLoading={isBasketUpdating}
          onChange={(newVal: number) => {
            setLocationQty(newVal);
            allowRequest.current = true;
          }}
          maxWidth="120px"
          ml="auto"
        />
      )}
      {item.info_required && (
        <IconButton
          iconName="Trash.Outline"
          _active={{ backgroundColor: "transparent" }}
          _hover={{ backgroundColor: "transparent" }}
          aria-label={t("Remove item", { ns: "common" })}
          disabled={isDisabled}
          iconColor="black"
          onClick={async () => {
            try {
              await actions.initModifyLocationPermissions(
                permission,
                item,
                decrementedItemGroupQty
              );
            } catch (error) {
              recordAppError(error);
            }
          }}
        />
      )}
    </Flex>
  );
};

export default LocationCartItem;
